import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchButtonWrap,
  SearchCols,
  SearchRows,
} from 'components/layouts/SearchBox';
import { ComboBox, MultiComboBox } from 'components/selects/ComboBox';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import { Code } from 'models/common/CommonCode';
import { SetupProject } from 'models/setup/SetupProject';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoading } from 'components/process/Loading';
import { useMessageBar } from 'hooks/useMessageBar';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { findProject } from 'apis/setup/SetupProject';
import {
  SetupUnloadingDashboard,
  SetupUnloadingDashboardCondition,
} from 'models/setup/mobile/UnloadingDashboard';
import { SetupSchedule } from 'models/setup/SetupSchedule';
import { findExcpByElmPrjId } from 'apis/setup/SetupSchedule';
import { Box, Card, CardContent, Grid, Stack, Typography } from '@mui/material';
import { findUnloadingItemList, findUnloadingBoxList } from 'apis/setup/mobile/UnloadingDashboard';
import { Sort, ViewInAr } from '@mui/icons-material';
import useSessionStore from 'stores/useSessionStore';
import { useNavigate } from 'react-router-dom';

interface chkCondition {
  elmPrjId: boolean;
}

const SetupUnloadingDashboardPage = () => {
  const { t } = useTranslation();
  const { openLoading } = useLoading();
  const { openMessageBar } = useMessageBar();
  const { elmPrjId } = useSessionStore();
  const navigate = useNavigate();

  const defaultCondition = {
    bildPlntCd: '',
    bildPlntCds: [],
    elmPrjId: elmPrjId || '',
    elmPrjNm: '',
    excpId: '',
    excpNm: '',
    lblIssuYn: '',
    searchDataInsUserId: '',
  };
  const lblIssuData: Code[] = [
    {
      cmnCd: 'Y',
      cmnCdNm: 'Y',
    },
    {
      cmnCd: 'N',
      cmnCdNm: 'N',
    },
  ];
  const [bildPlntCode, setBildPlntCode] = useState<Code[]>([]);
  const [prjMasterData, setPrjMasterData] = useState<SetupProject[]>([]);
  const [excpData, setExcpData] = useState<any>([]);
  const [condition, setCondition] = useState<SetupUnloadingDashboardCondition>({
    ...defaultCondition,
  });
  const [error, setError] = useState<chkCondition>({ elmPrjId: false });
  const [slctProject, setSlctProject] = useState<string>('');

  const [itemInfo, setItemInfo] = useState<SetupUnloadingDashboard>({});
  const [boxInfo, setBoxInfo] = useState<SetupUnloadingDashboard>({});
  // const [itemStatCodeList, setItemStatCodeList] = useState<Code[]>([]);
  // const [boxStatCodeList, setBoxStatCodeList] = useState<Code[]>([]);

  const initCodes = async () => {
    const bildPlntCode = await getCommonCodeNames('FA_SU_PLNT_CD');
    setBildPlntCode(bildPlntCode);
    /**
     * 코드 매핑이 여의치 않아 주석처리 / 하드코딩으로 대체
     */
    // const itemCode = await getCommonCodeNames('ITEM_STATUS');
    // setItemStatCodeList(itemCode);
    // const boxCode = await getCommonCodeNames('BOX_STATUS');
    // setBoxStatCodeList(boxCode);

    findProject().then((result: SetupProject[]) => {
      if (result !== null) {
        setPrjMasterData(result);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
  };

  const projectOptions = useMemo(() => {
    return (prjMasterData || [])
      .filter((o) => {
        // elmPrjId와 elmPrjNm이 있는 항목만 포함
        if (!o.elmPrjId || !o.elmPrjNm) return false;
        // 플랜트 필터 적용
        return (condition?.bildPlntCds || []).length > 0
          ? condition.bildPlntCds.includes(o.bildPlntCd)
          : true;
      })
      .map((cur) => ({
        cmnCd: cur.elmPrjId!,
        cmnCdNm: cur.elmPrjNm!,
      }));
  }, [prjMasterData, condition?.bildPlntCds]);

  const excpOptions = useMemo(() => {
    return (excpData || []).reduce(
      (acc, cur) => [
        ...acc,
        {
          cmnCd: cur.excpId,
          cmnCdNm: cur.excpNm,
        } as Code,
      ],
      [] as Code[]
    );
  }, [excpData]);

  const handleOnChange = (name: string, value: any) => {
    if (name === 'elmPrjId') {
      setCondition((prev) => ({ ...prev, [name]: value, excpId: '' }));
    } else {
      setCondition((prev) => ({ ...prev, [name]: value }));
    }
  };

  const onSearch = () => {
    // validate
    if (!condition.elmPrjId) {
      setError({ elmPrjId: true });
      return;
    }

    openLoading(true);
    Promise.all([findUnloadingItemList(condition), findUnloadingBoxList(condition)])
      .then(([_ITEM, _BOX]) => {
        setItemInfo({ ..._ITEM });
        setBoxInfo({ ..._BOX });
        setError({ elmPrjId: false });
      })
      .finally(() => openLoading(false));
  };

  const onChgPrj = () => {
    if (condition.elmPrjId) {
      findExcpByElmPrjId(condition.elmPrjId).then((res: SetupSchedule[]) => {
        setExcpData(res?.map((o) => ({ ...o })) || []);
      });
    } else {
      setExcpData([]);
    }
  };

  const onMovePage = (gbn = 'item') => {
    navigate(`/setup/excp/${gbn}`, {
      state: {
        elmPrjId: condition.elmPrjId,
        excpId: condition.excpId,
      },
    });
  };

  useEffect(() => {
    initCodes();
    onSearch();
  }, []);

  useEffect(() => {
    if (condition.elmPrjId) {
      findExcpByElmPrjId(condition.elmPrjId).then((res: SetupSchedule[]) => {
        setExcpData(res?.map((o) => ({ ...o })) || []);
      });
    } else {
      setExcpData([]);
    }
    onSearch();
  }, [condition.elmPrjId]);

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows className="FourCol">
              <SearchCols>
                <label>{String(t('setup.label.플랜트', '플랜트'))}</label>
                <MultiComboBox
                  id="bildPlntCd"
                  options={bildPlntCode}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={condition?.bildPlntCds || []}
                  onChange={(value) => {
                    handleOnChange('bildPlntCds', value);
                    // 플랜트 선택 변경 시, 현재 선택된 프로젝트가 새로운 플랜트 필터에 포함되지 않으면 프로젝트 선택 초기화
                    if (
                      !(prjMasterData || []).filter(
                        (o) =>
                          condition?.elmPrjId === o.elmPrjId &&
                          ((value || []).length < 1 || (value || []).includes(o.bildPlntCd || ''))
                      ).length
                    ) {
                      handleOnChange('elmPrjId', '');
                    }
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>
                  <span className={'dot'}>{t('setup.label.프로젝트명', '프로젝트명')}</span>
                </label>
                <ComboBox
                  placeholder={String(
                    t('com.label.프로젝트를 선택해주세요', '프로젝트를 선택해주세요')
                  )}
                  options={projectOptions}
                  defaultValue={condition?.elmPrjId}
                  onChange={(value) => {
                    handleOnChange('elmPrjId', value);
                  }}
                  isError={error.elmPrjId}
                  msgError={String(
                    t('setup.label.프로젝트명을 선택해 주세요.', '프로젝트명을 선택해 주세요.')
                  )}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('setup.label.협력명', '협력명'))}</label>
                <ComboBox
                  placeholder={String(t('com.label.전체', '전체'))}
                  options={excpOptions}
                  defaultValue={condition?.excpId}
                  onChange={(value) => {
                    handleOnChange('excpId', value);
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('setup.label.바코드관리', '바코드관리'))}</label>
                <ComboBox
                  placeholder={String(t('com.label.전체', '전체'))}
                  options={lblIssuData}
                  defaultValue={condition?.lblIssuYn}
                  onChange={(value) => {
                    handleOnChange('lblIssuYn', value);
                  }}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <SearchBoxButtons
              defaultCondition={defaultCondition}
              setReset={setCondition}
              onSearch={onSearch}
            />
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>
      <Grid container sx={{ py: 4, gap: 0 }}>
        {/* Section 1 */}
        <Grid item xs={12} md={6} sx={{ pr: { md: 2 } }}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
            <Sort />
            품목 현황
          </Typography>
          <Stack spacing={2.5}>
            <Box sx={{ p: 2, border: '2px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Grid container spacing={3}>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onMovePage('item')}
                >
                  <ProgressCard
                    title="Packing 대기"
                    total={itemInfo.allCnt || 0}
                    count={itemInfo.pakWait || 0}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onMovePage('item')}
                >
                  <ProgressCard
                    title="Packing 완료"
                    total={itemInfo.allCnt || 0}
                    count={itemInfo.pakDone || 0}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ p: 2, border: '2px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Grid container spacing={3}>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onMovePage('item')}
                >
                  <ProgressCard
                    title="컨테이너 완료"
                    total={itemInfo.allCnt || 0}
                    count={itemInfo.ctnrDone || 0}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onMovePage('item')}
                >
                  <ProgressCard
                    title="도착처리 완료"
                    total={itemInfo.allCnt || 0}
                    count={itemInfo.arivDone || 0}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onMovePage('item')}
                >
                  <ProgressCard
                    title="Fab-In 완료"
                    total={itemInfo.allCnt || 0}
                    count={itemInfo.fabinDone || 0}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onMovePage('item')}
                >
                  <ProgressCard
                    title="이상처리"
                    total={itemInfo.allCnt || 0}
                    count={itemInfo.abnormal || 0}
                    theme="error"
                  />
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </Grid>

        {/* Section 2 */}
        <Grid item xs={12} md={6} sx={{ pl: { md: 2 } }}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
            <ViewInAr />
            Box 현황
          </Typography>
          <Stack spacing={2.5}>
            <Box sx={{ p: 2, border: '2px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Grid container spacing={3}>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onMovePage('box')}
                >
                  <ProgressCard
                    title="Packing 완료"
                    total={boxInfo.allCnt || 0}
                    count={boxInfo.pakDone || 0}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onMovePage('box')}
                >
                  <ProgressCard
                    title="컨테이너 완료"
                    total={boxInfo.allCnt || 0}
                    count={boxInfo.ctnrDone || 0}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ p: 2, border: '2px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Grid container spacing={3}>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onMovePage('box')}
                >
                  <ProgressCard
                    title="도착처리 완료"
                    total={boxInfo.allCnt || 0}
                    count={boxInfo.arivDone || 0}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onMovePage('box')}
                >
                  <ProgressCard
                    title="Fab-In 완료"
                    total={boxInfo.allCnt || 0}
                    count={boxInfo.fabinDone || 0}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onMovePage('box')}
                >
                  <ProgressCard
                    title="이상처리"
                    total={boxInfo.allCnt || 0}
                    count={boxInfo.abnormal || 0}
                    theme="error"
                  />
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </Grid>
      </Grid>
    </>
  );
};

interface CardData {
  title: string;
  total: number;
  count: number;
  theme?: 'default' | 'error';
}

const ProgressCard = ({ title, total, count, theme = 'default' }: CardData) => {
  const background = theme === 'default' ? '#F7F9F8' : '#FFF2CC';
  const color = theme === 'default' ? 'text-sky-500' : 'text-red-500';
  const progress = total > 0 ? ((count / total) * 100).toFixed(0) : 0;

  return (
    <Card
      sx={{
        height: '20vh',
        minHeight: '140px',
        display: 'flex',
        flexDirection: 'column',
        background,
      }}
    >
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h6" align="center" gutterBottom>
          {title}
        </Typography>
        <Stack
          direction="row"
          spacing={3}
          sx={{ mt: 'auto', mb: 3, justifyContent: 'space-around', alignItems: 'center' }}
        >
          <Typography variant="body2" sx={{ fontSize: '1.7rem', textAlign: 'center' }}>
            <span className={`font-bold ${color}`}>{progress}%</span>
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '1rem', textAlign: 'center' }}>
            {`${count} / ${total}` || '0 / 0'}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default SetupUnloadingDashboardPage;
